import React, { useState } from 'react';
import { ChevronRightIcon, MailIcon, MapPinIcon, StarIcon } from 'lucide-react';
import BackButton from '../UI/BackButton';
import { useBrand } from '../../hooks/useBrand';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
export interface AuthorHeroStat {
  value: string | number;
  label: string;
  /** Show a star after the value (ratings) */
  star?: boolean;
  /** Emphasise the value in the brand's secondary accent */
  highlight?: boolean;
}
interface AuthorHeroProps {
  name: string;
  avatar: string;
  bio: string;
  genres: string[];
  stats: AuthorHeroStat[];
  /** Blurred backdrop image; falls back to the avatar */
  backdrop?: string;
  location?: string;
  /** Small status chip on the avatar, e.g. "Open for Events" */
  status?: string;
  /** Leading badge before the genres, e.g. an AfterTide tier range */
  leadBadge?: string;
  showContact?: boolean;
}

/**
 * AuthorHero — the LitLaunch author profile header, shared by both platforms.
 * All author content arrives as props; colours come from the brand theme.
 */
export const AuthorHero: React.FC<AuthorHeroProps> = ({
  name,
  avatar,
  bio,
  genres,
  stats,
  backdrop,
  location,
  status,
  leadBadge,
  showContact = true
}) => {
  const {
    theme
  } = useBrand();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  return <div className="relative overflow-hidden" data-id="element-3367" data-mp="lvwz3zi65ahu">
      {/* Blurred backdrop */}
      <div className="absolute inset-0 z-0" aria-hidden="true" data-id="element-3368" data-mp="gwu5rcbjwspp">
        <div className={`absolute inset-0 z-10 opacity-90 ${dk.page}`} data-id="element-3369" data-mp="gxdblunxfqnd" />
        <img src={backdrop ?? avatar} alt="" className="w-full h-full object-cover filter blur-3xl opacity-50 scale-110" data-id="element-3370" data-mp="bzbtbbybrvcy" />
      </div>

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-id="element-3371" data-mp="2wsud2xnu2vz">
        <div className="mb-6" data-id="element-3372" data-mp="3ohv3kx7umehg">
          <BackButton className={`${dk.textSecondary} ${rt.textHover}`} data-id="element-3373" data-mp="3ogtpskzg7dw1" />
        </div>

        <div className="flex flex-col md:flex-row gap-8 items-start" data-id="element-3374" data-mp="3thr58pt2pttu">
          {/* Avatar */}
          <div className="flex-shrink-0 relative" data-id="element-3375" data-mp="3fhoo55slb4fn">
            <div className={`w-32 h-32 md:w-48 md:h-48 rounded-full border-4 overflow-hidden shadow-xl ${dk.border}`} data-id="element-3376" data-mp="3fj9lt4a7b3hq">
              <img src={avatar} alt={name} className="w-full h-full object-cover" data-id="element-3377" data-mp="bzy99n3dlmby" />
            </div>
            {status && <div className={`absolute -bottom-2 -right-2 px-3 py-1 rounded-full text-xs font-bold shadow-md flex items-center gap-1 ${dk.isDark ? 'bg-green-900/80 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-3378" data-mp="2b6jnllsfe47">
                <span className="w-2 h-2 rounded-full bg-green-500" data-id="element-3379" data-mp="uwzdh5p3vieu" />
                {status}
              </div>}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0" data-id="element-3380" data-mp="193j7ivgn2i7m">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-id="element-3381" data-mp="19281eozq0ydc">
              <div data-id="element-3382" data-mp="1516d337ce6q5">
                <h1 className={`text-3xl sm:text-4xl font-bold tracking-tight mb-2 ${dk.textHeading}`} data-id="element-3383" data-mp="1iu4eb66gut1m">
                  {name}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-sm mb-4" data-id="element-3384" data-mp="1dxngtlddn2bv">
                  {location && <span className="flex items-center gap-1.5" data-id="element-3385" data-mp="m4em07lk6nbj">
                      <MapPinIcon className={`w-4 h-4 ${dk.textMuted}`} data-id="element-3386" data-mp="m67uztjn9jmp" />
                      <span className={dk.textSecondary} data-id="element-3387" data-mp="104pgdkm2uk1g">{location}</span>
                    </span>}
                  {leadBadge && <span className={`px-3 py-1 text-xs font-bold uppercase tracking-wider ${theme.tagRadius} ${theme.accent.solidBadge}`} data-id="element-3388" data-mp="2bit78hxdbs2a">
                      {leadBadge}
                    </span>}
                  {genres.map(genre => <span key={genre} className={`px-3 py-1 text-sm font-medium border ${theme.tagRadius} ${dk.cardSubtle} ${rt.textPrimary}`} data-id="element-3389" data-mp="l1fs0l4vtiao">
                      {genre}
                    </span>)}
                </div>
              </div>

              <div className="flex gap-3" data-id="element-3390" data-mp="z0xmnh4uzpry">
                {showContact && <button className={`px-5 py-2.5 rounded-lg text-sm font-medium shadow-sm flex items-center gap-2 transition-colors ${rt.primaryButton}`} data-id="element-3391" data-mp="1401vu7rs9nia">
                    <MailIcon className="w-4 h-4" data-id="element-3392" data-mp="1dr4yhe0d2ajo" />
                    Contact
                  </button>}
                <button onClick={() => setIsFollowing(f => !f)} aria-pressed={isFollowing} className={`px-5 py-2.5 rounded-lg text-sm font-medium border transition-colors ${isFollowing ? rt.secondaryButton : `${dk.border} ${dk.textPrimary} ${dk.dropdownItemHover}`}`} data-id="element-3393" data-mp="1htjdfn0wsq5o">
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
              </div>
            </div>

            {/* Bio */}
            <div className="mb-6 max-w-3xl" data-id="element-3394" data-mp="2tacghx8zyeio">
              <p className={`leading-relaxed ${dk.textSecondary} ${isBioExpanded ? '' : 'line-clamp-2'}`} data-id="element-3395" data-mp="2tbnpqcl7oeh6">
                {bio}
              </p>
              <button onClick={() => setIsBioExpanded(v => !v)} aria-expanded={isBioExpanded} className={`text-sm font-medium mt-1 inline-flex items-center ${rt.accentLink}`} data-id="element-3396" data-mp="3lyrvwkw1iyuj">
                {isBioExpanded ? 'Read Less' : 'Read More'}
                <ChevronRightIcon className={`w-3 h-3 ml-0.5 transition-transform duration-200 ${isBioExpanded ? 'rotate-90' : ''}`} data-id="element-3397" data-mp="djzv4nto6y1j" />
              </button>
            </div>

            {/* Stats */}
            <dl className={`flex flex-wrap gap-6 sm:gap-10 pt-6 border-t ${dk.divider}`} data-id="element-3398" data-mp="wn1w9k40a89t">
              {stats.map(stat => <div key={stat.label} className="flex flex-col-reverse" data-id="element-3399" data-mp="iv58fs9dekj8">
                  <dt className={`text-xs uppercase tracking-wider font-medium ${dk.textMuted}`} data-id="element-3400" data-mp="mwgph9e6hxft">
                    {stat.label}
                  </dt>
                  <dd className="flex items-center gap-1 mb-1" data-id="element-3401" data-mp="dwtzrkutag2l">
                    <span className={`text-2xl font-bold ${stat.highlight ? rt.accentSecondaryText : dk.textPrimary}`} data-id="element-3402" data-mp="du7iuum16o7p">
                      {stat.value}
                    </span>
                    {stat.star && <StarIcon className="w-5 h-5 fill-current text-amber-400" data-id="element-3403" data-mp="1ubbppapq7npb" />}
                  </dd>
                </div>)}
            </dl>
          </div>
        </div>
      </div>
    </div>;
};
export default AuthorHero;